import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { useTheme } from "@/components/ThemeProvider";

const NETWORK = [
    { l: "Protocol Overview", to: "/network" },
    { l: "Compute", to: "/network#compute" },
    { l: "Storage", to: "/storage" },
    { l: "AI Cloud", to: "/ai-compute" },
    { l: "Layer-1", to: "/layer-1" },
    { l: "Nodes", to: "/nodes" },
];
const DEVELOPERS = [
    { l: "Whitepaper", to: "/whitepaper" },
    { l: "Documentation", to: "https://decloudx.gitbook.io/decloudx", external: true },
    { l: "SDKs", to: "/developers#sdks" },
    { l: "Smart Contracts", to: "/layer-1#contracts" },
    { l: "Blog", to: "/blog" },
    { l: "GitHub", to: "https://x.com/DeCloudX_com", external: true },
];
const COMPANY = [
    { l: "About", to: "/about" },
    { l: "Patent", to: "/about" },
    { l: "Blog", to: "/blog" },
    { l: "Press", to: "/contact?role=partner" },
    { l: "Careers", to: "/contact?role=community" },
    { l: "Contact", to: "/contact" },
];

export default function Footer() {
    const { theme } = useTheme();
    return (
        <footer className="relative border-t border-line bg-card pt-14 sm:pt-20 pb-10" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
                <div className="grid grid-cols-2 md:grid-cols-12 gap-x-6 gap-y-10 mb-12 sm:mb-16">
                    <div className="col-span-2 md:col-span-5">
                        <Logo variant={theme === "dark" ? "white" : "gradient"} className="h-9 sm:h-10 mb-5" />
                        <p className="text-[13.5px] sm:text-sm text-muted-ink max-w-sm leading-relaxed">
                            Cloud infrastructure for Internet 3.0. A patent-filed hybrid-decentralized cloud protocol — decentralized compute, AI workloads, encrypted storage, and Layer-1 settlement in one resilient stack.
                        </p>
                        <div className="mt-6 flex items-center gap-3">
                            <a href="https://x.com/DeCloudX_com" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full surface flex items-center justify-center hover:border-line-strong hover:bg-card-muted transition-all" aria-label="X / Twitter" data-testid="footer-x">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className="text-ink">
                                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                </svg>
                            </a>
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">@DeCloudX_com</span>
                        </div>
                    </div>
                    <FooterColumn title="Network" items={NETWORK} prefix="network" />
                    <FooterColumn title="Developers" items={DEVELOPERS} prefix="dev" />
                    <FooterColumn title="Company" items={COMPANY} prefix="company" cols="md:col-span-3" />
                </div>

                <div className="pt-8 border-t border-line flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
                    <p className="font-heading font-bold text-base md:text-lg text-ink">
                        DeCloudX — <span className="text-muted-ink">Cloud infrastructure for Internet 3.0.</span>
                    </p>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">
                        <span>© {new Date().getFullYear()} DeCloudX Labs</span>
                        <Link to="/about" className="hover:text-ink transition-colors">Privacy</Link>
                        <Link to="/about" className="hover:text-ink transition-colors">Terms</Link>
                        <span className="text-brand-cyan">Patent Filed</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}

function FooterColumn({ title, items, prefix, cols = "md:col-span-2" }) {
    return (
        <div className={cols}>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-faint-ink mb-4">{title}</div>
            <ul className="space-y-3">
                {items.map((it) => (
                    <li key={it.l}>
                        {it.external ? (
                            <a href={it.to} target="_blank" rel="noreferrer" className="text-sm text-ink-2 hover:text-ink transition-colors" data-testid={`footer-${prefix}-${it.l.toLowerCase().replace(/[^a-z0-9]/g, "")}`}>
                                {it.l}
                            </a>
                        ) : (
                            <Link to={it.to} className="text-sm text-ink-2 hover:text-ink transition-colors" data-testid={`footer-${prefix}-${it.l.toLowerCase().replace(/[^a-z0-9]/g, "")}`}>
                                {it.l}
                            </Link>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}
